import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiErr } from "@/lib/api";
import { useI18n } from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { CalendarRange, Eye, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

export const PERIODS = ["Septembre 2026", "Octobre 2026", "Novembre 2026", "Décembre 2026", "Janvier 2027", "Février 2027", "Mars 2027", "Avril 2027", "Mai 2027", "Juin 2027", "Juillet 2027", "Août 2027"];
const money = (v) => `${Number(v || 0).toLocaleString("fr-FR")} Dhs`;

export default function MonthlyBatch() {
  const { t } = useI18n();
  const { hasRole } = useAuth();
  const navigate = useNavigate();
  const canManage = hasRole("super_admin", "direction", "administration", "finance");
  const [form, setForm] = useState({ period_label: "", issue_date: "", due_date: "", group_code: "all", exclude_already_invoiced: true });
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const payload = () => ({
    period_label: form.period_label,
    issue_date: form.issue_date || undefined,
    due_date: form.due_date || undefined,
    group_code: form.group_code === "all" ? undefined : form.group_code,
    exclude_already_invoiced: form.exclude_already_invoiced,
  });

  const doPreview = async () => {
    if (!form.period_label) { toast.error(t("periodRequired")); return; }
    setLoading(true); setResult(null);
    try { const r = await api.post("/finance/monthly-batch/preview", payload()); setPreview(r.data); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  const doConfirm = async () => {
    setConfirmOpen(false); setLoading(true);
    try { const r = await api.post("/finance/monthly-batch/confirm", payload()); setResult(r.data); setPreview(null); toast.success(`${r.data.created_count} ${t("invoices")}`); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  if (!canManage) return <div className="p-8 text-center text-slate-400" data-testid="access-denied">{t("accessDenied")}</div>;

  return (
    <div data-testid="monthly-batch-page">
      <div className="flex items-center gap-3 mb-4"><CalendarRange className="h-6 w-6 text-primary" /><h1 className="text-2xl font-display font-bold text-slate-900">{t("monthlyBatch")}</h1></div>

      <Card className="card-float border-slate-200 p-4 mb-4">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <div><label className="text-xs text-slate-500">{t("period")}</label>
            <Select value={form.period_label} onValueChange={(v) => setForm((f) => ({ ...f, period_label: v }))}>
              <SelectTrigger data-testid="mb-period"><SelectValue placeholder={t("selectPeriod")} /></SelectTrigger>
              <SelectContent>{PERIODS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent></Select></div>
          <div><label className="text-xs text-slate-500">{t("issueDate")}</label><Input type="date" data-testid="mb-issue" value={form.issue_date} onChange={(e) => setForm((f) => ({ ...f, issue_date: e.target.value }))} /></div>
          <div><label className="text-xs text-slate-500">{t("dueDate")}</label><Input type="date" data-testid="mb-due" value={form.due_date} onChange={(e) => setForm((f) => ({ ...f, due_date: e.target.value }))} /></div>
          <div><label className="text-xs text-slate-500">{t("filterGroup")}</label>
            <Select value={form.group_code} onValueChange={(v) => setForm((f) => ({ ...f, group_code: v }))}>
              <SelectTrigger data-testid="mb-group"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">{t("allActiveStudents")}</SelectItem>{["N1", "N2", "N3", "N4"].map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}</SelectContent></Select></div>
        </div>
        <label className="flex items-center gap-2 text-sm text-slate-500 mt-3"><input type="checkbox" checked={form.exclude_already_invoiced} onChange={(e) => setForm((f) => ({ ...f, exclude_already_invoiced: e.target.checked }))} data-testid="mb-exclude-dup" />{t("excludeAlreadyInvoiced")}</label>
        <div className="mt-3"><Button className="gap-2" data-testid="mb-preview-btn" disabled={loading} onClick={doPreview}><Eye className="h-4 w-4" />{t("previewGeneration")}</Button></div>
      </Card>

      {preview && (
        <div data-testid="mb-preview">
          <div className="grid sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-4">
            {[["studentsCount", preview.students_count], ["familiesCount", preview.families_count], ["totalGross", money(preview.total_gross)], ["totalDiscount", `-${money(preview.total_discount)}`], ["totalExemption", `-${money(preview.total_exemption)}`], ["totalNet", money(preview.total_net)]].map(([k, v]) => (
              <Card key={k} className="card-float border-slate-200 p-3"><div className="text-slate-500 text-xs">{t(k)}</div><div className="text-lg font-bold text-slate-900">{v}</div></Card>
            ))}
          </div>

          <Card className="card-float border-slate-200 overflow-hidden mb-4">
            <div className="px-4 py-2 text-sm font-semibold text-slate-700 bg-slate-50/70">{t("included")} ({preview.included.length})</div>
            <Table>
              <TableHeader><TableRow className="bg-slate-50/70"><TableHead>{t("student")}</TableHead><TableHead>{t("family")}</TableHead><TableHead>{t("group")}</TableHead><TableHead className="text-right">{t("totalGross")}</TableHead><TableHead className="text-right">{t("totalDiscount")}</TableHead><TableHead className="text-right">{t("totalNet")}</TableHead></TableRow></TableHeader>
              <TableBody>{preview.included.length === 0 ? <TableRow><TableCell colSpan={6} className="text-center text-slate-400 py-6">{t("noData")}</TableCell></TableRow> :
                preview.included.map((i) => <TableRow key={i.student_id} data-testid="mb-inc-row"><TableCell className="text-slate-700 text-sm">{i.student_name}</TableCell><TableCell className="text-slate-500 text-sm">{i.family_name}</TableCell><TableCell>{i.group_code}</TableCell><TableCell className="text-right">{money(i.gross)}</TableCell><TableCell className="text-right text-emerald-600">-{money(i.discount + i.exemption)}</TableCell><TableCell className="text-right font-semibold">{money(i.net)}</TableCell></TableRow>)}
              </TableBody>
            </Table>
          </Card>

          {preview.excluded.length > 0 && (
            <Card className="card-float border-amber-200 overflow-hidden mb-4" data-testid="mb-excluded">
              <div className="px-4 py-2 text-sm font-semibold text-amber-700 bg-amber-50">{t("exclusions")} ({preview.excluded.length})</div>
              <Table>
                <TableHeader><TableRow><TableHead>{t("student")}</TableHead><TableHead>{t("family")}</TableHead><TableHead>{t("reason")}</TableHead></TableRow></TableHeader>
                <TableBody>{preview.excluded.map((e, idx) => <TableRow key={idx} data-testid="mb-exc-row"><TableCell className="text-slate-700 text-sm">{e.student_name}</TableCell><TableCell className="text-slate-500 text-sm">{e.family_name || "—"}</TableCell><TableCell className="text-amber-700 text-sm">{e.reason}</TableCell></TableRow>)}</TableBody>
              </Table>
            </Card>
          )}

          <Button className="gap-2" data-testid="mb-confirm-btn" disabled={preview.included.length === 0 || loading} onClick={() => setConfirmOpen(true)}><CheckCircle2 className="h-4 w-4" />{t("confirmGeneration")}</Button>
        </div>
      )}

      {result && (
        <Card className="card-float border-emerald-200 p-5" data-testid="mb-result">
          <div className="flex items-center gap-2 mb-3"><CheckCircle2 className="h-6 w-6 text-emerald-600" /><h2 className="text-lg font-semibold text-emerald-700">{t("generationResult")}</h2></div>
          <div className="grid sm:grid-cols-4 gap-3 text-sm">
            <div><div className="text-slate-500">{t("invoicesCreated")}</div><div className="text-xl font-bold text-slate-900" data-testid="mb-created-count">{result.created_count}</div></div>
            <div><div className="text-slate-500">{t("totalNet")}</div><div className="text-xl font-bold text-slate-900">{money(result.total_net)}</div></div>
            <div><div className="text-slate-500">{t("exclusions")}</div><div className="text-xl font-bold text-amber-600">{result.excluded.length}</div></div>
            <div><div className="text-slate-500">{t("totalDiscount")}</div><div className="text-xl font-bold text-emerald-600">-{money(result.total_discount)}</div></div>
          </div>
          <Button variant="outline" className="mt-4" data-testid="mb-view-invoices" onClick={() => navigate(`/finance/invoices?period=${encodeURIComponent(result.period_label)}`)}>{t("viewInvoices")}</Button>
        </Card>
      )}

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent data-testid="mb-confirm-dialog">
          <DialogHeader><DialogTitle>{t("confirmGeneration")}</DialogTitle>
            <DialogDescription>{t("confirmGenerationMsg")}</DialogDescription></DialogHeader>
          <DialogFooter><Button data-testid="mb-confirm-final-btn" onClick={doConfirm}>{t("confirmGeneration")}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
